import { collection, doc, setDoc } from "firebase/firestore";
import type { CollectionReference, DocumentData } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Offline-safe Firestore writes.
 *
 * Firestore write promises (`addDoc`/`setDoc`/`updateDoc`/`deleteDoc`) only
 * resolve once the server acknowledges the write. While offline they stay
 * pending until reconnect — even though the change is already applied to the
 * local cache and will sync automatically. `await`ing them therefore hangs the
 * UI (a save button spins forever, navigation never happens) despite the data
 * being safely persisted.
 *
 * These helpers fire the write and return immediately so navigation and
 * form-close aren't blocked. Being offline is not an error: an offline write
 * doesn't reject, it waits. A write that *does* reject was refused by the
 * server (rules, a doc deleted underneath it), and Firestore has already
 * rolled the optimistic change back out of the local cache — so without a
 * word to the user, their edit just silently vanishes. Failures are therefore
 * logged and also broadcast to `onWriteFailure` listeners, which the app uses
 * to show a notice (see ErrorContext).
 */

export interface WriteFailure {
  /** What was being saved, as passed to `fireWrite`. */
  context: string;
  /** A sentence fit to show the user as-is. */
  message: string;
  error: unknown;
}

type WriteFailureListener = (failure: WriteFailure) => void;

// Module-level rather than context: `fireWrite` is called from plain helper
// functions (useSegments, activityLogging) with no React tree in reach.
const failureListeners = new Set<WriteFailureListener>();

/** Subscribe to rejected writes. Returns an unsubscribe function. */
export function onWriteFailure(listener: WriteFailureListener): () => void {
  failureListeners.add(listener);
  return () => {
    failureListeners.delete(listener);
  };
}

function reportFailure(context: string, error: unknown): void {
  console.error(`Firestore write failed (${context})`, error);
  // Writes don't normally reject for lack of network — they queue — but be
  // sure we never tell someone on ship wifi their change was refused when it's
  // only waiting.
  const code = (error as { code?: unknown } | null)?.code;
  if (code === "unavailable") return;
  const failure: WriteFailure = {
    context,
    message: `A change couldn't be saved (${context}) — it was rejected by the server.`,
    error,
  };
  for (const listener of failureListeners) {
    try {
      listener(failure);
    } catch (e) {
      console.error("Write-failure listener threw", e);
    }
  }
}

/** Fire a write without awaiting the server ack; report any eventual failure. */
export function fireWrite(op: Promise<unknown>, context: string): void {
  void op.catch((e: unknown) => reportFailure(context, e));
}

/**
 * Create a document with a client-generated id and return the id synchronously.
 * Works offline: the id exists immediately and the write persists locally.
 */
export function createLocalDoc(
  colRef: CollectionReference<DocumentData>,
  data: DocumentData,
  context = `new item in ${colRef.id}`,
): string {
  const ref = doc(colRef);
  fireWrite(setDoc(ref, data), context);
  return ref.id;
}

/** Convenience: a collection reference by path, for use with createLocalDoc. */
export function col(path: string, ...segments: string[]): CollectionReference<DocumentData> {
  return collection(db, path, ...segments);
}
